import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, EyeOff, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/ApiService';
import { ENDPOINTS } from '../constant/ApiEndpoints';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [password, setPassword] = useState({
    "new_password": "",
    "confirm_password": "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter your Password.");
      return;
    }

  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(password.new_password)) {
      toast.error(
        "Password must be at least 8 characters long and include at least one number and one letter."
      );
      return;
    }
    if (password.new_password !== password.confirm_password) {
      toast.error("Password and Confirm password does not match.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await api.post(ENDPOINTS.resetPassword, { "password": password.new_password });
      const result = response?.data;
      if (response?.status == 200 || response?.status == 201) {
        toast.success(`Password has been updated`);
      }
    } catch (error) {
      console.error("Change password error", error);
    } finally {
      setIsLoading(false);
      setPassword({
        "old_password": "",
        "new_password": ""
      });
      onClose();
    }
  };

  const backdropVariants = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { y: "-50px", opacity: 0 },
    visible: { y: "0", opacity: 1, transition: { type: "spring", stiffness: 300, damping: 30 } },
    exit: { y: "50px", opacity: 0 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold font-display text-gray-800">Change Password</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                <X size={24} />
              </button>
            </div>
            <p className="text-gray-500 mb-6">
              Enter your current and new password to update your account credentials
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={confirmPassword ? "text" : "password"}
                  placeholder="Enter New Password"
                  value={password.new_password}
                  onChange={(e) =>
                    setPassword((prev) => ({ ...prev, new_password: e.target.value }))
                  }
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setConfirmPassword(prev => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {confirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Confirm Password"
                  value={password.confirm_password}
                  onChange={(e) =>
                    setPassword((prev) => ({ ...prev, confirm_password: e.target.value }))
                  }
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <button
                disabled={isLoading}
                type="submit"
                className="w-full bg-brand-red text-white py-3 rounded-lg font-semibold hover:bg-red-600 focus:outline-none focus:ring-4 focus:ring-brand-red/50 transition-all shadow-md hover:shadow-lg"
              >
                {isLoading ? 'Loading..' : 'Change Password'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChangePasswordModal;
